import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Container, Graphics } from '@pixi/react'
import { ColorSource, Graphics as PixiGraphics, Container as PixiContainer, FederatedWheelEvent } from 'pixi.js'
import { calculateScrollBarDimensions, calculateAxisValues, getContentSize, calculateScrollPercentage, calculateScrollBarSize } from '../util/scrollUtil'

const DEFAULT_GAME_RESOLUTION = { width: 1080, height: 1920 }
const DEFAULT_BAR_STYLE = { color: 0xcccccc, backgroundColor: 0xcccccc, showBarBackground: false }

interface ScrollProps {
  x: number
  y: number
  width: number
  height: number
  children: ReactNode[]
  speed?: number
  background?: {
    color: ColorSource
    alpha: number
  }
  disableWheel?: boolean
  barStyle?: {
    color: ColorSource
    backgroundColor: ColorSource
    showBarBackground: boolean
  }
  isVertical?: boolean
  inertiaEnabled?: boolean
  inertiaDeceleration?: number
  gameResolution?: {
    width: number
    height: number
  }
}
export type ScrollBoxImperativeMethod = {
  scrollTo: ({ x, y }: { x?: number, y?: number }) => void
}

export const ScrollBox = forwardRef(({
  x,
  y,
  width,
  height,
  children,
  speed = 0.6,
  background,
  disableWheel = false,
  barStyle = DEFAULT_BAR_STYLE,
  isVertical = true,
  inertiaEnabled = true,
  inertiaDeceleration = 0.95,
  gameResolution = DEFAULT_GAME_RESOLUTION,
}: ScrollProps, ref) => {
  const containerRef = useRef<PixiContainer>(null)
  const maskRef = useRef(null)
  const scrollBarRef = useRef<PixiGraphics>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startPosition, setStartPosition] = useState(0)
  const [startScroll, setStartScroll] = useState(0)
  const [contentSize, setContentSize] = useState(0)
  const [scaleRatio, setScaleRatio] = useState(1)
  const targetScroll = useRef(0)
  const currentScroll = useRef(0)
  const animationRef = useRef<number | null>(null)
  const lastMousePosition = useRef(0)
  const lastMouseTime = useRef(0)
  const velocity = useRef(0)
  const isInertiaActive = useRef(false)
  const dragStartTime = useRef(0)
  const dragDistance = useRef(0)
  const scrollBarWidth = 5
  const scrollBarBorderRadius = 5

  const { axisMax, scrollAxis, getClientPosition } = calculateAxisValues(isVertical, height, width)

  const normalizeToOriginScale = useCallback((value: number) => {
    return value * (1 / scaleRatio)
  }, [scaleRatio])

  const drawRoundedRect = (
    g: PixiGraphics,
    x: number,
    y: number,
    width: number,
    height: number,
    color: ColorSource,
  ) => {
    g.beginFill(color)
    g.drawRoundedRect(x, y, width, height, scrollBarBorderRadius)
    g.endFill()
  }

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (contentSize <= axisMax) return

    // 停止任何正在進行的慣性滾動
    isInertiaActive.current = false
    velocity.current = 0

    setIsDragging(true)
    const currentPosition = getClientPosition(e)
    setStartPosition(currentPosition)
    setStartScroll(currentScroll.current)

    // 初始化速度追踪和拖動數據
    lastMousePosition.current = currentPosition
    const currentTime = Date.now()
    lastMouseTime.current = currentTime
    dragStartTime.current = currentTime
    dragDistance.current = 0
  }, [contentSize, getClientPosition, axisMax])

  const updateScrollBarPosition = useCallback(() => {
    if (!scrollBarRef.current) return

    scrollBarRef.current.clear()
    const isScrollable = contentSize > axisMax

    if (isScrollable) {
      const maxContainerSize = axisMax

      const scrollPercentage = calculateScrollPercentage(contentSize, axisMax, currentScroll.current)
      const scrollBarSize = calculateScrollBarSize(axisMax, contentSize)
      const scrollBarPos = scrollPercentage * (maxContainerSize - scrollBarSize)

      const { barX, barY, barWidth, barHeight, bgX, bgY, bgWidth, bgHeight } = calculateScrollBarDimensions(
        isVertical,
        scrollBarWidth,
        scrollBarSize,
        scrollBarPos,
        width,
        height,
        maxContainerSize,
      )

      const { backgroundColor, showBarBackground, color } = barStyle

      if (showBarBackground) {
        drawRoundedRect(scrollBarRef.current, bgX, bgY, bgWidth, bgHeight, backgroundColor)
      }

      drawRoundedRect(scrollBarRef.current, barX, barY, barWidth, barHeight, color)
    }
  }, [barStyle, contentSize, height, isVertical, axisMax, width])

  const animateScroll = useCallback(() => {
    if (!containerRef.current) return

    const diff = (targetScroll.current - currentScroll.current)

    if (Math.abs(diff) > 0.5) {
      currentScroll.current += diff * speed
      containerRef.current[scrollAxis] = currentScroll.current

      updateScrollBarPosition()
      animationRef.current = requestAnimationFrame(animateScroll)
    }
    else {
      currentScroll.current = targetScroll.current
      containerRef.current[scrollAxis] = currentScroll.current
      animationRef.current = null
    }
  }, [scrollAxis, speed, updateScrollBarPosition])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        const currentPosition = getClientPosition(e)
        const currentTime = Date.now()
        const delta = normalizeToOriginScale(currentPosition - startPosition)
        const maxScroll = -(contentSize - axisMax)

        // 計算速度 (像素/毫秒)
        const timeDelta = currentTime - lastMouseTime.current
        if (timeDelta > 0) {
          const positionDelta = normalizeToOriginScale(currentPosition - lastMousePosition.current)
          velocity.current = positionDelta / timeDelta

          // 累計拖動距離（取絕對值以獲得總移動距離）
          dragDistance.current += Math.abs(positionDelta)
        }

        // 更新最後位置和時間
        lastMousePosition.current = currentPosition
        lastMouseTime.current = currentTime

        targetScroll.current = Math.min(0, Math.max(maxScroll, startScroll + delta))
        if (!animationRef.current) {
          animationRef.current = requestAnimationFrame(animateScroll)
        }
      }
    },
    [isDragging, getClientPosition, normalizeToOriginScale, startPosition, contentSize, axisMax, startScroll, animateScroll],
  )

  const handleWheel = useCallback(
    (e: FederatedWheelEvent) => {
      if (disableWheel) return
      e.preventDefault()
      const delta = normalizeToOriginScale(e.deltaY)
      const maxScroll = -(contentSize - axisMax)

      targetScroll.current = Math.min(0, Math.max(maxScroll, currentScroll.current - delta))
      if (!animationRef.current) {
        animationRef.current = requestAnimationFrame(animateScroll)
      }
    },
    [disableWheel, normalizeToOriginScale, contentSize, axisMax, animateScroll],
  )

  const handleMouseUp = useCallback(() => {
    if (isDragging && inertiaEnabled && Math.abs(velocity.current) > 0.1) {
      // 啟動慣性滾動
      isInertiaActive.current = true

      // 計算拖動時長（毫秒）
      const dragDuration = Date.now() - dragStartTime.current

      // 動態計算慣性係數
      // 1. 根據拖動時長計算時間係數：較長的拖動時間會產生更強的慣性
      const timeFactor = Math.min(Math.max(dragDuration / 300, 0.5), 2.0)

      // 2. 根據拖動距離計算距離係數：較長的拖動距離會產生更強的慣性
      const distanceFactor = Math.min(Math.max(dragDistance.current / 100, 0.5), 2.5)

      // 3. 結合時間和距離係數
      const inertiaPower = timeFactor * distanceFactor

      // 4. 計算動態減速率：較強的慣性會有較慢的減速率（但確保在合理範圍內）
      const dynamicDeceleration = Math.max(0.9, Math.min(0.98, inertiaDeceleration + (inertiaPower * 0.01)))

      // 5. 計算初始慣性距離係數
      const inertiaFactor = Math.max(15, Math.min(30, 15 * inertiaPower))

      const applyInertia = () => {
        if (!isInertiaActive.current) return

        // 計算慣性滾動
        const maxScroll = -(contentSize - axisMax)
        const inertiaDistance = velocity.current * inertiaFactor // 使用動態計算的慣性係數

        // 更新目標滾動位置
        targetScroll.current = Math.min(0, Math.max(maxScroll, currentScroll.current + inertiaDistance))

        // 使用動態減速率
        velocity.current *= dynamicDeceleration

        // 當速度足夠小時停止慣性
        if (Math.abs(velocity.current) < 0.05) {
          isInertiaActive.current = false
        }
        else {
          requestAnimationFrame(applyInertia)
        }

        // 確保滾動動畫正在運行
        if (!animationRef.current) {
          animationRef.current = requestAnimationFrame(animateScroll)
        }
      }

      applyInertia()
    }

    // 重置拖動數據
    dragDistance.current = 0
    setIsDragging(false)
  }, [animateScroll, contentSize, axisMax, isDragging, inertiaEnabled, inertiaDeceleration])

  useEffect(() => {
    const container = containerRef.current
    const mask = maskRef.current

    if (container && mask) {
      container.mask = mask

      setContentSize(getContentSize(isVertical, container))

      if (contentSize <= axisMax) {
        targetScroll.current = 0
        currentScroll.current = 0
        container[scrollAxis] = 0
      }

      updateScrollBarPosition()
    }
  }, [height, width, contentSize, updateScrollBarPosition, children, isVertical, axisMax, scrollAxis])

  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
    }
  }, [])

  // 設置遊戲自適應解析度後的縮放比
  useEffect(() => {
    function setCurrentRatio() {
      if (gameResolution) {
        const settingRatio = gameResolution.width / gameResolution.height
        const currentWindowRatio = window.innerWidth / window.innerHeight

        if (currentWindowRatio < settingRatio) {
          setScaleRatio(window.innerWidth / gameResolution.width)
        }
        else {
          setScaleRatio(window.innerHeight / gameResolution.height)
        }
      }
    }
    setCurrentRatio()
    window.addEventListener('resize', setCurrentRatio)
    return () => {
      window.removeEventListener('resize', setCurrentRatio)
    }
  }, [gameResolution])

  useImperativeHandle(ref, (): ScrollBoxImperativeMethod => ({
    scrollTo: ({ x, y }) => {
      if (!containerRef.current) return

      if (typeof x === 'number' && !isVertical) {
        // 計算水平滾動的最大範圍
        const maxScroll = -(contentSize - width)
        const minScroll = 0

        // 檢查 x 是否在可滾動範圍內
        if (x < maxScroll || x > minScroll) return

        targetScroll.current = x
        currentScroll.current = x
        containerRef.current.x = x
      }

      if (typeof y === 'number' && isVertical) {
        // 計算垂直滾動的最大範圍
        const maxScroll = -(contentSize - height)
        const minScroll = 0

        // 檢查 y 是否在可滾動範圍內
        if (y < maxScroll || y > minScroll) return

        targetScroll.current = y
        currentScroll.current = y
        containerRef.current.y = y
      }

      updateScrollBarPosition()
    },
  }))

  return (
    <Container
      name="ScrollBox"
      position={[x, y]}
      eventMode="static"
      onmousemove={handleMouseMove}
      ontouchmove={handleMouseMove}
      onmouseup={handleMouseUp}
      onmouseleave={handleMouseUp}
      ontouchend={handleMouseUp}
      onmousedown={handleMouseDown}
      ontouchstart={handleMouseDown}
      onwheel={handleWheel}
    >
      <Graphics
        name="ClickArea"
        draw={(g) => {
          g.clear()
          g.beginFill(background ? background.color : 0xffffff)
          g.drawRect(0, 0, width, height)
          g.endFill()
        }}
        alpha={background ? background.alpha : 0}
      />
      <Graphics
        name="Mask"
        draw={(g) => {
          g.clear()
          g.beginFill(0xffffff)
          g.drawRect(0, 0, width, height)
          g.endFill()
        }}
        ref={maskRef}
      />
      <Container name="ScrollBoxItems" ref={containerRef}>
        {children.map((child, i) => (
          <Container key={i}>
            {child}
          </Container>
        ))}
      </Container>
      <Graphics name="Bar" ref={scrollBarRef} />
    </Container>
  )
})
